import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
    ArrowUp,
    Lock,
    Loader2,
    Plus,
    Sparkles,
    X,
    Quote,
    Menu,
} from "lucide-react";
import { toast, Toaster } from "sonner";
import Disclaimer from "@/components/Disclaimer";
import AiResponse from "@/components/AiResponse";
import ConversationSidebar from "@/components/ConversationSidebar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SESSION_KEY = "realtalk_session_id";
const USER_TOKEN_KEY = "realtalk_user_token";
const MAX_LEN = 2000;

const STARTER_PROMPTS = [
    {
        key: "relationships",
        label: "Relationships",
        prompt: "Something feels off with someone close to me and I can't name it. Here's what's been happening: ",
    },
    {
        key: "career",
        label: "Career",
        prompt: "I'm torn about my job — staying feels safe, leaving feels scary. What I'm weighing is: ",
    },
    {
        key: "family",
        label: "Family",
        prompt: "There's a pattern with my family that keeps repeating and I'm tired of it. It usually plays out like: ",
    },
    {
        key: "self",
        label: "Self",
        prompt: "I keep showing up the same way and don't like it. The thing I notice about myself is: ",
    },
];

function makeSessionId() {
    return (
        (crypto?.randomUUID && crypto.randomUUID()) ||
        `sess-${Date.now()}-${Math.random().toString(36).slice(2)}`
    );
}
function getOrCreateSessionId() {
    let sid = localStorage.getItem(SESSION_KEY);
    if (!sid) {
        sid = makeSessionId();
        localStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
}
function getOrCreateUserToken() {
    let t = localStorage.getItem(USER_TOKEN_KEY);
    if (!t) {
        t =
            (crypto?.randomUUID && crypto.randomUUID()) ||
            `usr-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        localStorage.setItem(USER_TOKEN_KEY, t);
    }
    return t;
}

export default function Chat() {
    const [sessionId, setSessionId] = useState(() => getOrCreateSessionId());
    const [userToken] = useState(() => getOrCreateUserToken());
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [sidebarRefreshKey, setSidebarRefreshKey] = useState(0);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const [unlockingId, setUnlockingId] = useState(null);
    const [subscribing, setSubscribing] = useState(false);
    const [passActive, setPassActive] = useState(false);
    const [passInfo, setPassInfo] = useState(null);
    const [refToken, setRefToken] = useState(null);
    const [refBannerVisible, setRefBannerVisible] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const scrollRef = useRef(null);

    const charCountWarn = input.length >= MAX_LEN * 0.85;
    const charCountBlock = input.length > MAX_LEN;

    const loadConversation = async (sid) => {
        try {
            const r = await axios.get(`${API}/chat/conversations/${sid}`);
            setMessages(r.data || []);
        } catch {
            setMessages([]);
        }
    };

    const loadPassStatus = async (sid) => {
        try {
            const r = await axios.get(`${API}/subscription/status/${sid}`);
            setPassActive(!!r.data?.active);
            setPassInfo(r.data || null);
        } catch {
            setPassActive(false);
            setPassInfo(null);
        }
    };

    const handleCancelSubscription = async () => {
        if (!passInfo?.active || passInfo?.kind !== "subscription") return;
        if (passInfo?.cancel_at_period_end) {
            toast("Already set to cancel at period end.");
            return;
        }
        try {
            await axios.post(
                `${API}/subscription/cancel?session_id=${encodeURIComponent(sessionId)}`,
            );
            toast.success("Subscription will cancel at period end.");
            loadPassStatus(sessionId);
        } catch {
            toast.error("Couldn't cancel right now.");
        }
    };

    useEffect(() => {
        loadConversation(sessionId);
        loadPassStatus(sessionId);
    }, [sessionId]);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTo({
                top: scrollRef.current.scrollHeight,
                behavior: "smooth",
            });
        }
    }, [messages.length, sending]);

    // Handle Stripe redirect (success or cancel) and capture ?ref=<token>
    useEffect(() => {
        const ref = searchParams.get("ref");
        if (ref) {
            setRefToken(ref);
            setRefBannerVisible(true);
            try {
                localStorage.setItem("realtalk_ref_token", ref);
            } catch {
                // ignore
            }
            // strip the param so sharing the URL again doesn't double-count
            const next = new URLSearchParams(searchParams);
            next.delete("ref");
            setSearchParams(next, { replace: true });
        } else if (localStorage.getItem("realtalk_ref_token")) {
            // show banner once per browser until user has sent their first msg
            setRefBannerVisible(true);
        }

        const stripeSessionId = searchParams.get("session_id");
        const cancelled = searchParams.get("cancelled");
        if (cancelled) {
            toast("Payment cancelled — no charge made.");
            setSearchParams({}, { replace: true });
            return;
        }
        if (!stripeSessionId) return;

        let attempts = 0;
        const max = 10;
        const poll = async () => {
            attempts += 1;
            try {
                const r = await axios.get(
                    `${API}/payments/status/${stripeSessionId}`,
                );
                if (r.data.payment_status === "paid") {
                    if (r.data.product_type === "monthly_pass") {
                        toast.success(
                            "Monthly Pass active — everything is unlocked for 30 days.",
                        );
                    } else {
                        toast.success("Unlocked! Full answer revealed.");
                    }
                    await loadConversation(sessionId);
                    await loadPassStatus(sessionId);
                    setSearchParams({}, { replace: true });
                    return;
                }
                if (r.data.status === "expired") {
                    toast.error("Payment session expired.");
                    setSearchParams({}, { replace: true });
                    return;
                }
            } catch {
                // ignore
            }
            if (attempts < max) setTimeout(poll, 2000);
            else {
                toast(
                    "Still processing… refresh in a moment if it doesn't update.",
                );
                setSearchParams({}, { replace: true });
            }
        };
        poll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const startNewConversation = () => {
        const sid = makeSessionId();
        localStorage.setItem(SESSION_KEY, sid);
        setSessionId(sid);
        setMessages([]);
        setPassActive(false);
        setPassInfo(null);
        setSidebarRefreshKey((k) => k + 1);
        toast("Fresh start.");
    };

    const switchConversation = (sid) => {
        if (sid === sessionId) return;
        localStorage.setItem(SESSION_KEY, sid);
        setSessionId(sid);
        setMessages([]);
    };

    const handleSend = async (e) => {
        e?.preventDefault();
        const text = input.trim();
        if (!text || sending) return;
        if (text.length > MAX_LEN) {
            toast.error(`Too long — keep it under ${MAX_LEN} characters.`);
            return;
        }
        setSending(true);
        setInput("");
        const optimistic = {
            id: `tmp-${Date.now()}`,
            role: "user",
            content: text,
            created_at: new Date().toISOString(),
            session_id: sessionId,
            is_unlocked: true,
        };
        setMessages((prev) => [...prev, optimistic]);
        try {
            const payload = {
                session_id: sessionId,
                message: text,
                user_token: userToken,
            };
            // Attach ref_token only on the first message of this session
            if (refToken && messages.length === 0) {
                payload.ref_token = refToken;
            }
            const r = await axios.post(`${API}/chat/send`, payload);
            setMessages((prev) => {
                const filtered = prev.filter((m) => m.id !== optimistic.id);
                return [...filtered, r.data.user_message, r.data.ai_message];
            });
            setSidebarRefreshKey((k) => k + 1);
            // ref is consumed after first message — clear and hide banner
            if (refToken && messages.length === 0) {
                try {
                    localStorage.removeItem("realtalk_ref_token");
                } catch {
                    // ignore
                }
                setRefToken(null);
                setRefBannerVisible(false);
            }
        } catch (err) {
            const status = err?.response?.status;
            if (status === 429) {
                toast.error(
                    err?.response?.data?.detail || "You're sending too fast.",
                );
            } else if (status === 400) {
                toast.error(err?.response?.data?.detail || "Couldn't send.");
            } else {
                toast.error("Couldn't reach the AI. Try again.");
            }
            setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
            setInput(text);
        } finally {
            setSending(false);
        }
    };

    const handleUnlock = async (messageId) => {
        setUnlockingId(messageId);
        try {
            const r = await axios.post(`${API}/payments/checkout`, {
                message_id: messageId,
                origin_url: window.location.origin,
                product_type: "single",
            });
            window.location.href = r.data.url;
        } catch {
            toast.error("Could not start checkout. Please try again.");
            setUnlockingId(null);
        }
    };

    const handleSubscribe = async () => {
        setSubscribing(true);
        try {
            const r = await axios.post(`${API}/payments/checkout`, {
                origin_url: window.location.origin,
                product_type: "monthly_pass",
                session_id: sessionId,
            });
            window.location.href = r.data.url;
        } catch {
            toast.error("Could not start checkout. Please try again.");
            setSubscribing(false);
        }
    };

    const fillStarter = (p) => {
        setInput(p);
    };

    return (
        <div className="min-h-screen bg-rt-base text-rt-text flex flex-col">
            <Toaster
                theme="dark"
                position="top-center"
                toastOptions={{
                    style: {
                        background: "#1A1523",
                        border: "1px solid #2B2438",
                        color: "#F3F0F8",
                    },
                }}
            />

            <ConversationSidebar
                open={sidebarOpen}
                onClose={() => setSidebarOpen(false)}
                userToken={userToken}
                currentSessionId={sessionId}
                onSelect={switchConversation}
                onNew={startNewConversation}
                refreshKey={sidebarRefreshKey}
            />

            <header className="sticky top-0 z-20 bg-rt-base/70 backdrop-blur-xl border-b border-white/5">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setSidebarOpen(true)}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-rt-border text-rt-muted hover:text-rt-text hover:border-rt-border/80 transition"
                            aria-label="Open conversations"
                            data-testid="open-sidebar-btn"
                        >
                            <Menu className="w-4 h-4" />
                        </button>
                        <Link
                            to="/"
                            className="inline-flex items-center gap-2 text-sm text-rt-muted hover:text-rt-text transition"
                            data-testid="chat-back-link"
                        >
                            <span className="font-serif text-base tracking-tight">
                                RealTalk
                            </span>
                        </Link>
                    </div>
                    <div className="flex items-center gap-3">
                        {passActive && (
                            <div className="hidden sm:flex items-center gap-2">
                                <span
                                    className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] text-[#B392F0] border border-[#B392F0]/30 bg-[#B392F0]/10 rounded-full px-3 py-1"
                                    data-testid="pass-active-badge"
                                >
                                    <Sparkles className="w-3 h-3" />
                                    {passInfo?.kind === "subscription"
                                        ? "Subscribed"
                                        : "Pass active"}
                                </span>
                                {passInfo?.kind === "subscription" &&
                                    !passInfo?.cancel_at_period_end && (
                                        <button
                                            onClick={handleCancelSubscription}
                                            className="text-[11px] text-rt-dim hover:text-rt-text transition"
                                            data-testid="cancel-subscription-btn"
                                        >
                                            Cancel
                                        </button>
                                    )}
                                {passInfo?.cancel_at_period_end && (
                                    <span className="text-[11px] text-rt-dim">
                                        ending soon
                                    </span>
                                )}
                            </div>
                        )}
                        <button
                            onClick={startNewConversation}
                            className="inline-flex items-center gap-1.5 text-xs text-rt-muted hover:text-rt-text transition rounded-full border border-rt-border px-3 py-1.5"
                            data-testid="new-conversation-btn"
                        >
                            <Plus className="w-3.5 h-3.5" />
                            New
                        </button>
                        <div className="hidden sm:flex text-xs text-rt-dim items-center gap-1.5">
                            <Lock className="w-3 h-3" /> Anonymous
                        </div>
                    </div>
                </div>
            </header>

            <div
                ref={scrollRef}
                className="flex-1 overflow-y-auto"
                data-testid="chat-scroll"
            >
                <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14 space-y-6">
                    {refBannerVisible && messages.length === 0 && (
                        <motion.div
                            initial={{ opacity: 0, y: -6 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="relative flex items-start gap-3 rounded-2xl border border-[#B392F0]/25 bg-[#B392F0]/[0.06] px-4 py-3 sm:px-5 sm:py-4"
                            data-testid="ref-banner"
                        >
                            <Quote className="mt-0.5 w-4 h-4 shrink-0 text-[#B392F0]" />
                            <div className="flex-1 text-sm leading-relaxed text-rt-muted">
                                You got here through someone else&rsquo;s
                                clarity.{" "}
                                <span className="text-rt-text">
                                    Now it&rsquo;s your turn — say what&rsquo;s
                                    actually on your mind.
                                </span>
                            </div>
                            <button
                                onClick={() => setRefBannerVisible(false)}
                                className="shrink-0 text-rt-dim hover:text-rt-text transition"
                                aria-label="Dismiss"
                                data-testid="ref-banner-close"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </motion.div>
                    )}
                    {messages.length === 0 && !sending && (
                        <motion.div
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="text-center py-12 sm:py-16"
                            data-testid="chat-empty-state"
                        >
                            <div className="font-serif text-3xl sm:text-4xl tracking-tight">
                                What&rsquo;s weighing on you?
                            </div>
                            <p className="mt-4 text-rt-muted max-w-md mx-auto leading-relaxed">
                                Type it out the way it actually feels. No
                                judgment. No script.
                            </p>

                            <div className="mt-10">
                                <div className="text-[11px] uppercase tracking-[0.25em] text-rt-dim mb-4">
                                    Or pick a thread
                                </div>
                                <div className="flex flex-wrap gap-2 justify-center">
                                    {STARTER_PROMPTS.map((s) => (
                                        <button
                                            key={s.key}
                                            onClick={() => fillStarter(s.prompt)}
                                            className="rounded-full border border-rt-border bg-rt-surface/70 hover:border-[#B392F0]/40 hover:bg-rt-card text-rt-text text-sm px-4 py-2 transition-all hover:-translate-y-[1px]"
                                            data-testid={`starter-chip-${s.key}`}
                                        >
                                            {s.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </motion.div>
                    )}

                    <AnimatePresence initial={false}>
                        {messages.map((m) => (
                            <motion.div
                                key={m.id}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4 }}
                                className={
                                    m.role === "user"
                                        ? "flex justify-end"
                                        : "flex justify-start"
                                }
                            >
                                {m.role === "user" ? (
                                    <div
                                        className="max-w-[85%] rounded-2xl rounded-br-md bg-white/5 backdrop-blur-md border border-white/10 px-5 py-3 text-rt-text text-[15px] leading-relaxed whitespace-pre-wrap"
                                        data-testid="user-bubble"
                                    >
                                        {m.content}
                                    </div>
                                ) : (
                                    <AiResponse
                                        message={m}
                                        onUnlock={handleUnlock}
                                        onSubscribe={handleSubscribe}
                                        unlocking={unlockingId === m.id}
                                        subscribing={subscribing}
                                        passActive={passActive}
                                    />
                                )}
                            </motion.div>
                        ))}
                    </AnimatePresence>

                    {sending && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="flex justify-start"
                            data-testid="thinking-indicator"
                        >
                            <div className="bg-rt-card rounded-2xl rounded-bl-md px-5 py-4 flex items-center gap-2 text-rt-muted text-sm">
                                <span className="rt-thinking-dot animate-pulse-soft" />
                                <span>Thinking…</span>
                            </div>
                        </motion.div>
                    )}
                </div>
            </div>

            <div className="sticky bottom-0 z-20 bg-gradient-to-t from-rt-base via-rt-base to-rt-base/70 backdrop-blur-xl border-t border-white/5">
                <form
                    onSubmit={handleSend}
                    className="max-w-3xl mx-auto px-4 sm:px-6 py-5"
                >
                    <div
                        className={`flex items-end gap-3 bg-rt-surface border rounded-2xl px-4 py-3 focus-within:ring-1 transition ${
                            charCountBlock
                                ? "border-red-500/50 focus-within:ring-red-500/50"
                                : "border-rt-border focus-within:ring-[#B392F0]/50"
                        }`}
                    >
                        <textarea
                            data-testid="chat-input"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSend(e);
                                }
                            }}
                            rows={1}
                            maxLength={MAX_LEN + 50}
                            placeholder="What's on your mind?"
                            className="flex-1 resize-none bg-transparent outline-none text-rt-text placeholder:text-rt-dim text-[15px] leading-relaxed max-h-40"
                            disabled={sending}
                        />
                        <button
                            type="submit"
                            disabled={
                                sending || !input.trim() || charCountBlock
                            }
                            data-testid="send-message-btn"
                            className="shrink-0 inline-flex items-center gap-2 rounded-full bg-[#B392F0] hover:bg-[#C8B1F5] disabled:opacity-40 disabled:hover:bg-[#B392F0] text-black font-medium text-sm px-4 py-2 transition-all duration-200 hover:-translate-y-[1px]"
                        >
                            {sending ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <ArrowUp className="w-4 h-4" />
                            )}
                            <span>Get Clarity</span>
                        </button>
                    </div>
                    {(charCountWarn || charCountBlock) && (
                        <div
                            className={`mt-2 text-[11px] text-right ${
                                charCountBlock ? "text-red-400" : "text-rt-dim"
                            }`}
                            data-testid="char-counter"
                        >
                            {input.length} / {MAX_LEN}
                        </div>
                    )}
                </form>
                <Disclaimer compact />
            </div>
        </div>
    );
}
